import * as React from 'react';
import { useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { toggleSimulationFilterThunk } from '../../actions/Simulation/SimulationFiltersActions';
import { EditorTopBarRight } from '../../components/Editor/EditorTopBar/EditorTopBarRight';
import { StateType } from '../../reducers';
import { activeTabIndexSelector } from '../../reducers/EditorReducer/EditorSelector';
import { dataIsSuperAlphaSelector } from '../../reducers/SimulationReducer/DataReducer/DataSelector';
import {
  filtersCodeSelector,
  filtersDataExplorerSelector,
  filtersLearnSelector,
  filtersResultsSelector,
  filtersSuperCodeSelector,
  filtersTutorialSelector,
} from '../../reducers/SimulationReducer/FiltersReducer/FiltersSelector';
import { SimulationFilter } from '../../reducers/SimulationReducer/FiltersReducer/FiltersReducer.types';
import { useHistory } from 'react-router-dom';
import type { History } from 'history';
import { getUserPermissionSet } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import analytics from '../../shared/services/analytics/analytics';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

const EditorChooserPanel = () => {
  const history = useHistory();

  // @ts-ignore
  const currentActiveTab = useSelector(activeTabIndexSelector);
  const filters = useSelector(
    (state: StateType) => ({
      code: filtersCodeSelector(state),
      superCode: filtersSuperCodeSelector(state),
      results: filtersResultsSelector(state),
      learn: filtersLearnSelector(state),
      dataExplorer: filtersDataExplorerSelector(state),
      tutorial: filtersTutorialSelector(state),
    }),
    shallowEqual
  );
  const superAlpha = useSelector(dataIsSuperAlphaSelector);
  const permissionSet = useSelector(getUserPermissionSet);

  const dispatch = useAppDispatch();
  const dispatchToggleSimulationFilter = useCallback(
    (filter: SimulationFilter, tabIndex: number, history: History<unknown>) => {
      dispatch(toggleSimulationFilterThunk(filter, tabIndex, history));
    },
    [dispatch]
  );

  const handleToggleFilter = (filter: SimulationFilter) => {
    analytics.trackCustomEvent('sim_toggle_tab', { event_label: filter });
    dispatchToggleSimulationFilter(filter, currentActiveTab, history);
  };

  const handleToggleCode = () => {
    const codeType = superAlpha ? 'superCode' : 'code';
    handleToggleFilter(codeType);
  };

  const handleToggleResults = () => {
    handleToggleFilter('results');
  };

  const handleToggleLearn = () => {
    handleToggleFilter('learn');
  };
  const handleToggleDataExplorer = () => {
    handleToggleFilter('dataExplorer');
  };
  const handleToggleTutorial = () => {
    handleToggleFilter('tutorial');
  };

  const isTutorialAvailable = permissionSet.has('TUTORIAL') || permissionSet.has('CONSULTANT');

  return (
    <EditorTopBarRight
      onClickCode={handleToggleCode}
      onClickResults={handleToggleResults}
      onClickLearn={handleToggleLearn}
      onClickDataExplorer={handleToggleDataExplorer}
      onClickTutorial={handleToggleTutorial}
      code={filters.superCode || filters.code}
      results={filters.results}
      learn={filters.learn}
      dataExplorer={filters.dataExplorer}
      isTutorialSelected={filters.tutorial}
      isTutorialAvailable={isTutorialAvailable}
    />
  );
};

export default EditorChooserPanel;
